import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

const EvaluationBar = ({ evaluation, isAnalyzing, orientation = 'white' }) => {
    const { percentage, displayText, color } = useMemo(() => {
        if (!evaluation) {
            return { percentage: 50, displayText: '0.00', color: '#888' };
        }

        let evalValue;

        if (evaluation.type === 'mate') {
            // Mate in N moves
            const mateIn = evaluation.value;
            if (mateIn > 0) {
                // White is winning (positive mate score) - White should take 100% (bottom when orientation is white)
                return {
                    percentage: 100, // White's portion is 100%
                    displayText: `M${mateIn}`,
                    color: '#fff'
                };
            } else {
                // Black is winning (negative mate score) - White should take 0% (black takes all)
                return {
                    percentage: 0, // White's portion is 0%
                    displayText: `M${Math.abs(mateIn)}`,
                    color: '#000'
                };
            }
        } else {
            // Centipawn evaluation - already from White's perspective
            evalValue = evaluation.value; // This is in pawns (e.g., 2.5 means White is up 2.5 pawns)

            // Cap evaluation at �10 for display purposes
            const cappedEval = Math.max(-10, Math.min(10, evalValue));

            // Convert to percentage representing WHITE's advantage (bottom section when viewing from white)
            // When White is winning (+10): white portion should be 100%
            // When Black is winning (-10): white portion should be 0%
            // When equal (0): white portion should be 50%
            const whitePercentage = ((cappedEval + 10) / 20) * 100;

            // Display text with sign
            const displayValue = evalValue > 0 ? `+${evaluation.displayValue}` : `-${evaluation.displayValue}`;

            return {
                percentage: whitePercentage,
                displayText: displayValue,
                color: evalValue > 0 ? '#fff' : '#000'
            };
        }
    }, [evaluation]);

    // When viewing from black's perspective, we need to flip the bar
    // If orientation is 'black', we want Black at bottom, so we need to invert
    const displayPercentage = orientation === 'black' ? 100 - percentage : percentage;

    // Determine which color is at the bottom based on orientation
    const bottomColor = orientation === 'white' ? '#fff' : '#000';
    const topColor = orientation === 'white' ? '#000' : '#fff';

    return (
        <Box sx={{ width: '60px', height: '100%', minHeight: '400px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
            <Tooltip title={isAnalyzing ? 'Analyzing...' : `Depth: ${evaluation?.depth || 0}`} placement="left">
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#333',
                        position: 'relative',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        border: '1px solid #555'
                    }}
                >
                    {/* Bottom section - color depends on orientation */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: `${displayPercentage}%`,
                            backgroundColor: bottomColor,
                            transition: 'height 0.3s ease-in-out',
                            borderTop: `1px solid ${bottomColor === '#fff' ? '#000' : '#fff'}`
                        }}
                    />

                    {/* Top section - opposite color */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: `${100 - displayPercentage}%`,
                            backgroundColor: topColor,
                            transition: 'height 0.3s ease-in-out'
                        }}
                    />

                    {/* Center line */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            right: 0,
                            height: '2px',
                            backgroundColor: '#666',
                            transform: 'translateY(-50%)',
                            zIndex: 1
                        }}
                    />

                    {/* Evaluation text */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 2,
                            backgroundColor: color === '#fff' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                            color: color === '#fff' ? '#000' : '#fff',
                            padding: '4px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            border: '1px solid ' + (color === '#fff' ? '#000' : '#fff'),
                            minWidth: '40px',
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 'bold' }}>
                            {displayText}
                        </Typography>
                    </Box>

                    {/* Analyzing indicator */}
                    {isAnalyzing && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: '5px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 3
                            }}
                        >
                            <Box
                                sx={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#4caf50',
                                    animation: 'pulse 1.5s infinite'
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Tooltip>

            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.3; }
                    }
                `}
            </style>
        </Box>
    );
};

export default EvaluationBar;